/* ✦ Instagram stories: hover previews on the gliding rail + full-screen shoppable story viewer. */
(function () {
  if (customElements.get('bz-stories')) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  class BzStories extends HTMLElement {
    connectedCallback() {
      try {
        this.stories = JSON.parse(this.querySelector('[data-bz-stories-data]').textContent);
      } catch (e) {
        this.stories = [];
      }
      this.viewer = this.querySelector('[data-bz-viewer]');
      this.video = this.querySelector('[data-bz-v-video]');
      this.frame = this.querySelector('[data-bz-frame]');
      this.bars = this.querySelector('[data-bz-bars]');
      this.index = 0;
      this.muted = true;

      // Move the viewer to <body> so no transformed ancestor can trap position:fixed.
      if (this.viewer && this.viewer.parentElement !== document.body) {
        this.viewerHome = this.viewer.parentElement;
        document.body.appendChild(this.viewer);
      }

      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-bz-story]');
        if (card) this.open(Number(card.dataset.bzStory), card);
      });

      this.initPosters();
      if (canHover && this.dataset.autoplayHover === 'true') this.initHoverPreview();
      this.initViewer();
    }

    disconnectedCallback() {
      if (this.viewer && this.viewer.parentElement === document.body) this.viewer.remove();
    }

    /* ---------- posters: load when the section nears the viewport ---------- */
    initPosters() {
      const load = () => this.querySelectorAll('img[data-src]').forEach((img) => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { load(); io.disconnect(); }
      }, { rootMargin: '600px 0px' });
      io.observe(this);
    }

    /* ---------- hover previews ---------- */
    initHoverPreview() {
      this.addEventListener('pointerover', (e) => {
        const btn = e.target.closest('.bz-rc');
        if (!btn || btn.contains(e.relatedTarget)) return;
        const v = btn.querySelector('video[data-src]');
        if (!v) return;
        if (!v.src) v.src = v.dataset.src + '#t=0.1';
        const p = v.play();
        if (p && p.then) p.then(() => btn.classList.add('is-playing')).catch(() => {});
      });
      this.addEventListener('pointerout', (e) => {
        const btn = e.target.closest('.bz-rc');
        if (!btn || btn.contains(e.relatedTarget)) return;
        const v = btn.querySelector('video');
        btn.classList.remove('is-playing');
        if (v && !v.paused) v.pause();
      });
    }

    /* ---------- viewer ---------- */
    initViewer() {
      const v = this.viewer;
      if (!v) return;
      v.querySelectorAll('[data-bz-v-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      v.querySelectorAll('[data-bz-v-prev]').forEach((b) => b.addEventListener('click', (e) => { e.stopPropagation(); this.go(this.index - 1); }));
      v.querySelectorAll('[data-bz-v-next]').forEach((b) => b.addEventListener('click', (e) => { e.stopPropagation(); this.go(this.index + 1); }));
      this.muteBtn = v.querySelector('[data-bz-v-mute]');
      this.pauseBtn = v.querySelector('[data-bz-v-pause]');
      this.muteBtn.addEventListener('click', () => this.toggleMute());
      this.pauseBtn.addEventListener('click', () => this.togglePause());
      v.addEventListener('click', (e) => { if (e.target === v) this.close(); });

      this.video.addEventListener('timeupdate', () => this.progress());
      this.video.addEventListener('ended', () => this.go(this.index + 1, true));

      // keyboard
      v.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowRight') this.go(this.index + 1);
        else if (e.key === 'ArrowLeft') this.go(this.index - 1);
        else if (e.key === ' ' && e.target === v) { e.preventDefault(); this.togglePause(); }
      });

      // touch: swipe left/right to navigate, swipe down to close, hold to pause
      let sx = 0, sy = 0, t0 = 0, holdTimer;
      this.frame.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        sx = t.clientX; sy = t.clientY; t0 = Date.now();
        holdTimer = setTimeout(() => { this.video.pause(); this.holding = true; }, 280);
      }, { passive: true });
      this.frame.addEventListener('touchend', (e) => {
        clearTimeout(holdTimer);
        const t = e.changedTouches[0];
        const dx = t.clientX - sx, dy = t.clientY - sy;
        if (this.holding) { this.holding = false; this.video.play().catch(() => {}); return; }
        if (dy > 90 && Math.abs(dy) > Math.abs(dx)) this.close();
        else if (Math.abs(dx) > 60 && Date.now() - t0 < 600) this.go(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      // build progress bars
      this.bars.innerHTML = this.stories.map(() => '<span class="bz-sviewer__bar"><i></i></span>').join('');
    }

    open(i, opener) {
      if (!this.stories.length) return;
      this.opener = opener;
      this.classList.add('is-paused');
      this.querySelectorAll('.bz-rc video').forEach((vid) => vid.pause());
      this.viewer.hidden = false;
      document.documentElement.classList.add('bz-viewer-open');
      requestAnimationFrame(() => this.viewer.classList.add('is-open'));
      this.viewer.setAttribute('tabindex', '-1');
      this.viewer.focus({ preventScroll: true });
      this.go(i, false, true);
    }

    close() {
      this.viewer.classList.remove('is-open');
      this.video.pause();
      document.documentElement.classList.remove('bz-viewer-open');
      this.classList.remove('is-paused');
      setTimeout(() => {
        this.viewer.hidden = true;
        this.video.removeAttribute('src');
        this.video.load();
      }, reduceMotion ? 0 : 300);
      if (this.opener) this.opener.focus({ preventScroll: true });
    }

    go(i, auto, first) {
      const n = this.stories.length;
      if (i >= n) {
        if (auto) return this.close();
        i = 0;
      }
      if (i < 0) i = n - 1;
      const swap = () => {
        this.index = i;
        const s = this.stories[i];
        [...this.bars.children].forEach((b, k) => {
          b.classList.toggle('is-done', k < i);
          b.querySelector('i').style.width = k < i ? '100%' : '0';
        });
        const ambient = this.viewer.querySelector('[data-bz-ambient]');
        if (ambient) ambient.style.backgroundImage = s.poster ? `url("${s.poster}")` : '';
        this.video.poster = s.poster || '';
        this.video.muted = this.muted;
        if (s.video) {
          this.video.src = s.video;
          this.video.play().catch(() => {
            // autoplay with sound blocked: fall back to muted
            this.muted = true;
            this.video.muted = true;
            this.syncMute();
            this.video.play().catch(() => {});
          });
        } else {
          this.video.removeAttribute('src');
        }
        this.syncMute();
        this.syncPause(false);
        const shop = this.viewer.querySelector('[data-bz-v-shop]');
        if (s.url) {
          shop.hidden = false;
          shop.href = s.url;
          this.viewer.querySelector('[data-bz-v-title]').textContent = s.linkTitle || s.title || '';
          this.viewer.querySelector('[data-bz-v-sub]').textContent = s.linkSub || '';
          this.viewer.querySelector('[data-bz-v-cta]').textContent = s.cta || '';
          this.viewer.querySelector('[data-bz-v-thumb]').style.backgroundImage = s.linkImg ? `url("${s.linkImg}")` : '';
        } else {
          shop.hidden = true;
        }
      };
      if (first || reduceMotion) return swap();
      this.frame.classList.remove('is-leaving');
      void this.frame.offsetWidth;
      this.frame.classList.add('is-leaving');
      setTimeout(swap, 170);
    }

    progress() {
      const bar = this.bars.children[this.index];
      if (!bar || !this.video.duration) return;
      bar.querySelector('i').style.width = `${(this.video.currentTime / this.video.duration) * 100}%`;
    }

    toggleMute() {
      this.muted = !this.muted;
      this.video.muted = this.muted;
      this.syncMute();
    }

    syncMute() {
      const icon = this.muted
        ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4v-5Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="m16 9.5 5 5m0-5-5 5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4v-5Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
      this.muteBtn.innerHTML = icon;
      this.muteBtn.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
    }

    togglePause() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
      this.syncPause(this.video.paused);
    }

    syncPause(paused) {
      this.pauseBtn.innerHTML = paused
        ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.5 5.5v13l10.5-6.5-10.5-6.5Z" fill="currentColor"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor"/></svg>';
      this.pauseBtn.setAttribute('aria-label', paused ? 'Play' : 'Pause');
    }
  }
  customElements.define('bz-stories', BzStories);
})();
